import { useEffect, useRef } from 'react';
import type { TableSelectedCell } from '@/components/custom/erp';
import type { ChiTietPhieuNhapKho } from '@/types/schemas';
import { useTableRows } from '@/components/custom/erp';
import { useDefaultExport } from './useDefaultExport';

export type SelectedCellInfo = TableSelectedCell;

export type DetailRow = ChiTietPhieuNhapKho;

export function useDetailRows(
  initialRows: DetailRow[] = [],
  onChange?: (rows: DetailRow[]) => void,
  onFieldValueChange?: (row: DetailRow, field: string, value: unknown) => Partial<DetailRow>,
  isCreateMode: boolean = false
) {
  const { applyDefaultExportDataToRows } = useDefaultExport();
  const {
    rows,
    setRows,
    selectedRowUuid,
    setSelectedRowUuid,
    selectedRow,
    setSelectedRow,
    selectedCell,
    setSelectedCell,
    handleRowClick,
    handleCellClick,
    clearSelection,
    handleCellValueChange,
    handleAddRow,
    handleDeleteRow,
    handleCopyRow,
    handleMoveRow,
    handlePasteRow,
    setNonWritableFields,
    setPasteConfigs
  } = useTableRows<DetailRow>({
    initialRows,
    defaultRows: 1,
    onChange,
    onFieldValueChange,
    defaultField: 'ma_vt'
  });

  const hasAppliedDefaultRef = useRef(false);
  const latestRowsRef = useRef<DetailRow[]>([]);

  useEffect(() => {
    latestRowsRef.current = rows;
  }, [rows]);

  useEffect(() => {
    if (!isCreateMode || hasAppliedDefaultRef.current) return;

    let alive = true;
    const applyDefaultExportData = async () => {
      const updatedRows = await applyDefaultExportDataToRows(latestRowsRef.current);
      if (!alive || !updatedRows) return;
      if (updatedRows !== latestRowsRef.current) {
        setRows(updatedRows as DetailRow[]);
      }
      hasAppliedDefaultRef.current = true;
    };

    applyDefaultExportData();
    return () => {
      alive = false;
    };
  }, [isCreateMode, applyDefaultExportDataToRows, setRows]);

  return {
    rows,
    setRows,
    selectedRowUuid,
    setSelectedRowUuid,
    selectedRow,
    setSelectedRow,
    selectedCell,
    setSelectedCell,
    handleRowClick,
    handleCellClick,
    clearSelection,
    handleCellValueChange,
    handleAddRow,
    handleDeleteRow,
    handleCopyRow,
    handlePasteRow,
    handleMoveRow,
    setNonWritableFields,
    setPasteConfigs
  };
}
